/* ------------------------------------------------------------------
   ColdSide cart drawer upgrades

   One job: adding and removing the free gift lines as the subtotal
   crosses each rung of the gift ladder, and playing the unlock effect.
   (The "Close the gap" row's Add button moved to assets/cs-card-added.js
   on 5 October 2026, with every other card's.)

   Everything the shopper reads is rendered by Liquid. This file never
   formats money, never writes copy and never computes a percentage. It
   decides whether a line should be added or removed, asks the Cart API,
   and then re-renders the drawer from the server so Liquid can state the
   new truth. That keeps one source of truth and avoids the classic bug
   where a client-side subtotal drifts away from the real cart.

   Two structural constraints shape the code:

   - The drawer replaces the whole of .drawer__inner on every cart
     change, so any listener bound directly to an element inside it dies
     on the next render. Every listener here is delegated from document.

   - The existing re-render paths in cart.js and cart-drawer.js are left
     completely alone. This file adds behaviour beside them, it does not
     wrap, patch or replace any of it.
   ------------------------------------------------------------------ */

(function () {
  'use strict';

  var DRAWER = '#CartDrawer';
  var INNER = '.drawer__inner';

  // Guards against re-entrancy. Our own re-render trips the observer that
  // triggers the gift check, so without a lock the reconcile could stack.
  var busy = false;

  // If adding a gift fails once (sold out, discount removed, network), stop
  // trying that variant for the rest of the page view rather than hammering
  // the API. Keyed by variant id so one rung failing leaves the other alone.
  var giftAddFailed = {};

  // Same idea for removal: a failing remove would otherwise retry on every
  // re-render its own attempt causes.
  var giftRemoveFailed = false;

  function routeFor(name, fallback) {
    return (window.routes && window.routes[name]) || fallback;
  }

  function postJSON(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart request failed with ' + response.status);
      return response.json();
    });
  }

  /* Re-render the drawer from the server.

     This mirrors what CartDrawerItems already does: pull the cart-drawer
     section and swap the inner HTML of .drawer__inner. Doing it the same way
     means the gift bar, the add-on row and the totals all refresh together
     and cannot disagree with each other. */
  function renderDrawer() {
    var url = routeFor('cart_url', '/cart') + '?section_id=cart-drawer';

    return fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (text) {
        var incoming = new DOMParser().parseFromString(text, 'text/html').querySelector(INNER);
        var current = document.querySelector(DRAWER + ' ' + INNER);
        if (!incoming || !current) return;
        current.innerHTML = incoming.innerHTML;
      });
  }

  /* ---------- free gift ---------- */

  /* The block writes one [data-cs-gift-rung] marker per live rung during
     render, so this reads the DOM rather than recomputing anything. If the
     block is absent the feature is off and there is nothing to do.

     Every rung is settled in one pass: lines that should come out go in a
     single update call, lines that should go in go in a single add call, then
     one re-render. Removals go first so a basket that fell below both rungs
     loses both gifts without an add in between. */
  /* The cart page renders the ladder too (main-cart-footer, 10 October
     2026). Its quantity updates redraw the page and not the drawer, so on
     /cart the page's copy is the current one and is read first. */
  function giftBlock() {
    return document.querySelector('#main-cart-footer [data-cs-gift]') || document.querySelector(DRAWER + ' [data-cs-gift]');
  }

  /* After a gift goes in or out on /cart, redraw the page's lines and
     footer the way Dawn's cart.js does, so the gift line shows without a
     reload. */
  function renderPage() {
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (!items || !footer || !items.dataset.id || !footer.dataset.id) return Promise.resolve();
    var ids = [items.dataset.id, footer.dataset.id];
    return fetch(routeFor('cart_url', '/cart') + '?sections=' + ids.join(','))
      .then(function (response) { return response.json(); })
      .then(function (sections) {
        [[items, ids[0]], [footer, ids[1]]].forEach(function (pair) {
          var html = sections[pair[1]];
          if (!html) return;
          var incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('.js-contents');
          var current = pair[0].querySelector('.js-contents');
          if (incoming && current) current.innerHTML = incoming.innerHTML;
        });
      })
      .catch(function (error) {
        console.error('[cs-cart] could not redraw the cart page', error);
      });
  }

  function reconcileGift() {
    if (busy) return;

    var block = giftBlock();
    if (!block) return;

    var adds = [];
    var removals = {};
    var hasRemovals = false;

    block.querySelectorAll('[data-cs-gift-rung]').forEach(function (rung) {
      var qualified = rung.getAttribute('data-cs-gift-qualified') === 'true';
      var inCart = rung.getAttribute('data-cs-gift-in-cart') === 'true';
      var variantId = rung.getAttribute('data-cs-gift-variant');
      var key = rung.getAttribute('data-cs-gift-key');

      if (qualified && !inCart && variantId && !giftAddFailed[variantId]) {
        /* The hidden _cs_gift property makes the free unit its own line, so
           earplugs the customer added themselves stay a separate paid line
           (snippets/cs-cart-gift-bar.liquid). */
        adds.push({ id: Number(variantId), quantity: 1, properties: { _cs_gift: '1' } });
      }

      /* Dropped back below this rung, so its gift comes out again. Removal is
         keyed on the line item key rather than a line number, which stays
         correct even when other lines shift around it. */
      if (!qualified && inCart && key && !giftRemoveFailed) {
        removals[key] = 0;
        hasRemovals = true;
      }
    });

    if (!adds.length && !hasRemovals) {
      settle(block);
      return;
    }

    busy = true;
    var work = Promise.resolve();

    if (hasRemovals) {
      work = work.then(function () {
        return postJSON(routeFor('cart_update_url', '/cart/update.js'), { updates: removals }).catch(function (error) {
          giftRemoveFailed = true;
          console.error('[cs-cart] could not remove a free gift', error);
        });
      });
    }

    if (adds.length) {
      work = work.then(function () {
        return postJSON(routeFor('cart_add_url', '/cart/add.js'), { items: adds }).catch(function (error) {
          adds.forEach(function (item) {
            giftAddFailed[item.id] = true;
          });
          console.error('[cs-cart] could not add a free gift', error);
        });
      });
    }

    work.then(function () {
      return Promise.all([renderDrawer(), renderPage()]);
    }).finally(function () {
      busy = false;
    });
  }

  /* ---------- unlock effect (5 October 2026) ----------

     When an add or a quantity change takes the spend over a gift threshold,
     the ladder plays once: a gold shine along the fill, the reached marker
     pops, eight sparkles burst from it, a gold toast slides in at the top of
     the card and the card glows, all for about 2.4s. With reduced motion
     only the toast shows, without sliding.

     It runs from settle(), which reconcileGift calls only once the cart
     needs no gift added or removed, so the gift's own re-render has already
     happened and cannot cut the effect off. The first settle after page
     load only records the spend: nothing plays on load. */
  var lastSpend = null;
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function settle(block) {
    var spend = Number(block.getAttribute('data-spend'));
    if (isNaN(spend)) return;
    var t1 = Number(block.getAttribute('data-t1')) || 0;
    var t2 = Number(block.getAttribute('data-t2')) || 0;
    var before = lastSpend;
    lastSpend = spend;
    if (before === null || spend <= before) return;

    var level = 0;
    if (t2 && before < t2 && spend >= t2) level = 2;
    else if (t1 && before < t1 && spend >= t1) level = 1;
    if (level) playUnlock(block, level);
  }

  function playUnlock(block, level) {
    var text = block.getAttribute(level === 2 ? 'data-toast2' : 'data-toast1') || '';
    var toast = document.createElement('div');
    toast.className = 'cs-ladder__toast';
    toast.setAttribute('role', 'status');
    toast.textContent = text;
    block.appendChild(toast);

    if (reducedMotion) {
      toast.classList.add('is-in');
      setTimeout(function () { toast.remove(); }, 2400);
      return;
    }

    /* Read layout once so the toast starts above the card, then slide in. */
    void toast.offsetHeight;
    toast.classList.add('is-in');
    block.classList.add('is-unlock', 'is-shine');

    var mark = block.querySelector('.cs-ladder__mark[data-level="' + level + '"]');
    var track = block.querySelector('.cs-ladder__track');
    if (mark) {
      mark.classList.add('is-pop');
      setTimeout(function () { mark.classList.remove('is-pop'); }, 700);
    }
    if (mark && track) {
      for (var i = 0; i < 8; i++) {
        var angle = (Math.PI * 2 * i) / 8 + Math.PI / 8;
        var spark = document.createElement('span');
        spark.className = 'cs-ladder__spark';
        spark.style.left = mark.style.left;
        spark.style.setProperty('--dx', Math.round(Math.cos(angle) * 30) + 'px');
        spark.style.setProperty('--dy', Math.round(Math.sin(angle) * 22) + 'px');
        track.appendChild(spark);
      }
      setTimeout(function () {
        Array.prototype.forEach.call(track.querySelectorAll('.cs-ladder__spark'), function (s) { s.remove(); });
      }, 1200);
    }

    setTimeout(function () { block.classList.remove('is-shine'); }, 1100);
    setTimeout(function () {
      toast.classList.remove('is-in');
      block.classList.remove('is-unlock');
      setTimeout(function () { toast.remove(); }, 400);
    }, 2400);
  }

  /* ---------- change options from the bag (10 October 2026) ----------
     snippets/cs-line-variant-picker.liquid prints, under a line, a toggle
     and a picker of every option value, with the product's variants as
     JSON. Picking a value finds the variant that matches every chosen
     option. If it exists, is in stock and is not the current one, the new
     variant goes in with the same quantity and properties, then the old
     line comes out (by its key, which survives other lines shifting), then
     the drawer and /cart redraw from the server. The gift ladder settles
     itself on that redraw (watchDrawer below). */
  function lvpData(root) {
    var el = root.querySelector('[data-cs-lvp-data]');
    try { return JSON.parse(el.textContent); } catch (e) { return { variants: [], properties: {} }; }
  }
  function lvpChosen(root) {
    var picked = [];
    root.querySelectorAll('[data-cs-lvp-opt][aria-pressed="true"]').forEach(function (b) {
      picked[Number(b.getAttribute('data-cs-lvp-opt'))] = b.getAttribute('data-value');
    });
    return picked;
  }
  function lvpMatch(variants, picked) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var ok = v.options.every(function (o, idx) { return picked[idx] === undefined || o === picked[idx]; });
      if (ok) return v;
    }
    return null;
  }
  /* Grey every value that, with the other current choices, has no
     variant in stock. */
  function lvpMarkOff(root) {
    var data = lvpData(root);
    var picked = lvpChosen(root);
    root.querySelectorAll('[data-cs-lvp-opt]').forEach(function (b) {
      var idx = Number(b.getAttribute('data-cs-lvp-opt'));
      var trial = picked.slice();
      trial[idx] = b.getAttribute('data-value');
      var v = lvpMatch(data.variants, trial);
      var off = !v || !v.available;
      b.classList.toggle('is-off', off);
      b.setAttribute('aria-disabled', off ? 'true' : 'false');
    });
  }
  function lvpSwap(root, variantId) {
    var data = lvpData(root);
    var key = root.getAttribute('data-key');
    var qty = Number(root.getAttribute('data-qty')) || 1;
    var props = {};
    Object.keys(data.properties || {}).forEach(function (k) {
      if (k !== '_cs_gift' && data.properties[k] !== null && data.properties[k] !== '') props[k] = data.properties[k];
    });
    var status = root.querySelector('[data-cs-lvp-status]');
    root.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Changing…';
    var item = { id: variantId, quantity: qty };
    if (Object.keys(props).length) item.properties = props;
    var updates = {};
    updates[key] = 0;
    return postJSON(routeFor('cart_add_url', '/cart/add.js'), { items: [item] })
      .then(function () {
        return postJSON(routeFor('cart_update_url', '/cart/update.js'), { updates: updates });
      })
      .then(function () {
        return Promise.all([renderDrawer(), renderPage()]);
      })
      .catch(function (error) {
        root.removeAttribute('aria-busy');
        if (status) status.textContent = 'That one could not be changed. Try again, or remove it and add the size you want.';
        console.error('[cs-cart] could not change the line', error);
      });
  }
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-cs-lvp-toggle]');
    if (toggle) {
      var root = toggle.closest('[data-cs-lvp]');
      var panel = root && root.querySelector('.cs-lvp__panel');
      if (!panel) return;
      var open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.hidden = open;
      if (!open) lvpMarkOff(root);
      return;
    }
    var opt = event.target.closest('[data-cs-lvp-opt]');
    if (!opt) return;
    var box = opt.closest('[data-cs-lvp]');
    if (!box || box.getAttribute('aria-busy') === 'true') return;
    if (opt.getAttribute('aria-disabled') === 'true') return;
    var idx = opt.getAttribute('data-cs-lvp-opt');
    box.querySelectorAll('[data-cs-lvp-opt="' + idx + '"]').forEach(function (b) {
      b.setAttribute('aria-pressed', b === opt ? 'true' : 'false');
    });
    lvpMarkOff(box);
    var v = lvpMatch(lvpData(box).variants, lvpChosen(box));
    if (!v || !v.available) return;
    if (String(v.id) === box.getAttribute('data-variant')) return;
    lvpSwap(box, v.id);
  });

  /* ---------- wiring ---------- */

  /* Watching the drawer for re-renders catches every path that can change the
     cart, whoever triggered it, without this file having to know which module
     did the work or which pub/sub event it published. */
  function watchDrawer() {
    var drawer = document.querySelector(DRAWER);
    var footer = document.getElementById('main-cart-footer');
    if (!drawer && !footer) return;

    var pending = null;
    var observer = new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(reconcileGift, 120);
    });

    if (drawer) observer.observe(drawer, { childList: true, subtree: true });
    if (footer) observer.observe(footer, { childList: true, subtree: true });
    reconcileGift();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawer);
  } else {
    watchDrawer();
  }
})();
